import Drawer from "../Drawer/Drawer";
import Filters from "./Filters";
import {
  Filter,
  FilterConfiguration,
  FilterDrawerConfiguration,
} from "./types";
import styles from "./FilterDrawer.module.scss";
import Button from "../Button/Button";
import { Fragment, useState } from "react";
import ConfigureFilter from "./ConfigureFilter/ConfigureFilter";
import AddFilter from "./AddFilter";
import { FilterGroup } from "./useFilters";
import { Filter as FilterIcon } from "../Icon/Icon";
import FiltersEmptyState from "./FiltersEmptyState";

type FilterGroupState =
  | {
      groupIndex: number;
      isAdding: true;
      isEditing?: false;
    }
  | {
      groupIndex: number;
      filterIndex: number;
      isEditing: true;
      configuration: FilterConfiguration;
      isAdding?: false;
    };

function FilterGroups({
  filterGroups,
  configuration,
  addFilter,
  editFilter,
  deleteFilter,
}: {
  filterGroups: Filter[][];
  configuration: FilterDrawerConfiguration[];
  addFilter: (filter: Filter, index: number) => void;
  editFilter: (filter: Filter, groupIndex: number, filterIndex: number) => void;
  deleteFilter: (groupIndex: number, filterIndex: number) => void;
}) {
  const [filterGroupState, setFilterGroupState] = useState<FilterGroupState>();
  const [selectedFilter, setSelectedFilter] = useState<FilterConfiguration>();

  if (filterGroupState?.isAdding) {
    return (
      <AddFilter
        configuration={configuration}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        onBack={() => setFilterGroupState(undefined)}
        onSave={(filter) => {
          addFilter(filter, filterGroupState.groupIndex);
          setFilterGroupState(undefined);
          setSelectedFilter(undefined);
        }}
      />
    );
  }

  if (filterGroupState?.isEditing) {
    return (
      <ConfigureFilter
        options={filterGroupState.configuration}
        onBack={() => setFilterGroupState(undefined)}
        onSave={(filter) => {
          editFilter(
            filter,
            filterGroupState.groupIndex,
            filterGroupState.filterIndex,
          );
          setFilterGroupState(undefined);
        }}
      />
    );
  }

  if (!filterGroups.length) {
    return (
      <FiltersEmptyState
        addFilter={() => setFilterGroupState({ isAdding: true, groupIndex: 0 })}
      />
    );
  }

  return filterGroups.map((filters, index) => {
    return (
      <Fragment key={index}>
        <Filters
          filters={filters}
          setIsAddingFilter={() =>
            setFilterGroupState({ isAdding: true, groupIndex: index })
          }
          setIsEditingFilter={(filterConfiguration, filterIndex) =>
            setFilterGroupState({
              isEditing: true,
              groupIndex: index,
              filterIndex,
              configuration: filterConfiguration,
            })
          }
          deleteFilter={(filterIndex) => deleteFilter(index, filterIndex)}
        />
        {index !== filterGroups.length - 1 && (
          <span className={styles.orSeparator}>OR</span>
        )}
        {index === filterGroups.length - 1 && !!filters.length && (
          <div>
            <Button
              variant="outline"
              size="xsmall"
              onClick={() =>
                setFilterGroupState({ isAdding: true, groupIndex: index + 1 })
              }
            >
              OR
            </Button>
          </div>
        )}
      </Fragment>
    );
  });
}

// type ExtractGeneric<Generic> = Generic extends FilterGroup<infer T>[]
//   ? T
//   : never;

type FilterDrawerProps = {
  filterGroups: FilterGroup[];
  configuration: FilterDrawerConfiguration[];
  isOpen: boolean;
  onClose: () => void;
  addFilter: (filter: Filter, index: number) => void;
  editFilter: (filter: Filter, groupIndex: number, filterIndex: number) => void;
  deleteFilter: (groupIndex: number, filterIndex: number) => void;
};

function FilterDrawer({
  filterGroups,
  configuration,
  isOpen,
  onClose,
  addFilter,
  editFilter,
  deleteFilter,
}: FilterDrawerProps) {
  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      FeaturedIconComponent={FilterIcon}
      title={`Filters (${filterGroups.reduce(
        (count, filter) => (count += filter.length),
        0,
      )})`}
    >
      <div className={styles.container}>
        <FilterGroups
          filterGroups={filterGroups}
          configuration={configuration}
          addFilter={addFilter}
          editFilter={editFilter}
          deleteFilter={deleteFilter}
        />
      </div>
    </Drawer>
  );
}

export default FilterDrawer;
